import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api, getApiErrorCode, getApiErrorMessage, isEmailVerificationRequiredError } from '@/services/api';
import { featureFlags } from '@/config/featureFlags';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  FileText, Upload, ArrowLeft, Loader2, Check, X, 
  AlertTriangle, TrendingUp, Target, Lightbulb, Info, Sparkles
} from 'lucide-react';
import MainMenu from '@/components/MainMenu';
import SavedResumeSelector from '@/components/SavedResumeSelector';
import {
  getUnsupportedUploadFormatMessage,
  isSupportedUploadFile,
  LEGACY_DOC_GUIDANCE,
  SUPPORTED_UPLOAD_FORMAT_LABEL,
  UPLOAD_ACCEPT,
} from '@/lib/uploadFormats';

// AI Disclosure Component
function AIDisclosure() {
  return (
    <div className="flex items-start gap-2 p-3 bg-accent/10 border border-accent/20 rounded-lg text-sm">
      <Sparkles className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
      <p className="text-muted-foreground">
        <span className="font-medium text-foreground">AI-generated suggestions.</span>{' '}
        Always review and verify before submitting a resume.
      </p>
    </div>
  );
}

export default function AnalysisPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const savedResumesEnabled = featureFlags.savedResumes;
  const effectivePlan = user?.effective_plan || user?.plan || 'free';
  
  const [resumeText, setResumeText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [savedResumesAvailable, setSavedResumesAvailable] = useState(savedResumesEnabled);
  const [savedResumes, setSavedResumes] = useState([]);
  const [savedResumesLoading, setSavedResumesLoading] = useState(false);
  const [loadingSavedResume, setLoadingSavedResume] = useState(false);
  const [selectedSavedResumeId, setSelectedSavedResumeId] = useState('');
  const [replacementNotice, setReplacementNotice] = useState('');
  const [pendingUploadedResumeSave, setPendingUploadedResumeSave] = useState(null);

  const savedResumeUiEnabled = savedResumesEnabled && savedResumesAvailable;

  const buildSavedResumeNameFromFilename = (filename) => {
    const cleanedFilename = (filename || '').trim();
    if (!cleanedFilename) {
      return undefined;
    }

    const filenameWithoutExtension = cleanedFilename.replace(/\.[^.]+$/, '').trim();
    return filenameWithoutExtension || cleanedFilename;
  };

  const hideSavedResumeUi = useCallback(() => {
    setSavedResumesAvailable(false);
    setSavedResumes([]);
    setSelectedSavedResumeId('');
    setPendingUploadedResumeSave(null);
  }, []);

  const refreshSavedResumes = useCallback(async ({ showErrorToast = false } = {}) => {
    if (!savedResumesEnabled || !savedResumesAvailable) {
      return;
    }

    setSavedResumesLoading(true);
    try {
      const data = await api.listSavedResumes();
      setSavedResumes(Array.isArray(data?.saved_resumes) ? data.saved_resumes : []);
    } catch (error) {
      if (getApiErrorCode(error) === 'saved_resumes_disabled') {
        hideSavedResumeUi();
        return;
      }

      if (showErrorToast) {
        toast.error(getApiErrorMessage(error, 'Failed to load saved resumes'));
      }
    } finally {
      setSavedResumesLoading(false);
    }
  }, [hideSavedResumeUi, savedResumesAvailable, savedResumesEnabled]);

  useEffect(() => {
    if (!savedResumesEnabled) {
      return;
    }

    refreshSavedResumes({ showErrorToast: true });
  }, [savedResumesEnabled, refreshSavedResumes]);

  const attemptSavePendingUploadedResume = async (currentResumeText) => {
    if (!savedResumeUiEnabled || !pendingUploadedResumeSave) {
      return currentResumeText;
    }

    const pendingResumeText = (pendingUploadedResumeSave.resumeText || '').trim();
    const currentTrimmedResumeText = (currentResumeText || '').trim();
    setPendingUploadedResumeSave(null);

    if (!pendingResumeText || pendingResumeText !== currentTrimmedResumeText) {
      return currentResumeText;
    }

    const payload = {
      resume_text: pendingResumeText,
      source: 'uploaded',
    };
    const savedResumeName = buildSavedResumeNameFromFilename(pendingUploadedResumeSave.filename);
    if (savedResumeName) {
      payload.name = savedResumeName;
    }

    try {
      const savedResume = await api.createSavedResume(payload);

      if (savedResume?.duplicate) {
        const existingResumeId = savedResume?.existing_resume?.id;
        if (existingResumeId) {
          try {
            const existingResume = await api.getSavedResume(existingResumeId);
            if (existingResume?.resume_text) {
              setResumeText(existingResume.resume_text);
              setSelectedSavedResumeId(existingResumeId);
              toast.info('This resume is already saved. We loaded the existing saved version instead.');
              await refreshSavedResumes();
              return existingResume.resume_text;
            }
          } catch (error) {
            if (getApiErrorCode(error) === 'saved_resumes_disabled') {
              hideSavedResumeUi();
              return currentResumeText;
            }

            toast.error(getApiErrorMessage(error, 'This resume is already saved, but the saved version could not be loaded right now.'));
            await refreshSavedResumes();
            return currentResumeText;
          }
        }

        toast.info('This resume is already saved. We loaded the existing saved version instead.');
        await refreshSavedResumes();
        return currentResumeText;
      }

      if (savedResume?.id) {
        setSelectedSavedResumeId(savedResume.id);
      }
      await refreshSavedResumes();
      return currentResumeText;
    } catch (error) {
      const errorCode = getApiErrorCode(error);

      if (errorCode === 'saved_resumes_disabled') {
        hideSavedResumeUi();
        return currentResumeText;
      }

      if (errorCode === 'saved_resume_limit_reached') {
        toast.info('Saved resume limit reached. You can keep using this uploaded resume, but it was not saved. Delete an old saved resume to save a new one.');
        return currentResumeText;
      }

      toast.error(getApiErrorMessage(error, 'Uploaded resume could not be saved for later.'));
      return currentResumeText;
    }
  };

  const handleSavedResumeSelect = async (savedResumeId) => {
    if (!savedResumeId) {
      return;
    }

    const hadExistingText = Boolean(resumeText.trim());

    setLoadingSavedResume(true);
    try {
      const savedResume = await api.getSavedResume(savedResumeId);
      setResumeText(savedResume.resume_text || '');
      setSelectedSavedResumeId(savedResumeId);
      setPendingUploadedResumeSave(null);
      setReplacementNotice(hadExistingText ? 'Previous text was replaced with the saved resume.' : '');
    } catch (error) {
      if (getApiErrorCode(error) === 'saved_resumes_disabled') {
        hideSavedResumeUi();
        return;
      }

      setSelectedSavedResumeId('');
      toast.error(getApiErrorMessage(error, 'Failed to load saved resume'));
    } finally {
      setLoadingSavedResume(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!isSupportedUploadFile(file)) {
      toast.error(getUnsupportedUploadFormatMessage());
      return;
    }

    const hadExistingText = Boolean(resumeText.trim());
    setUploadingFile(true);
    try {
      const data = await api.uploadResume(file);
      setResumeText(data.text);
      setReplacementNotice(hadExistingText ? 'Previous text was replaced with the uploaded resume.' : '');
      setSelectedSavedResumeId('');
      setPendingUploadedResumeSave(data.text?.trim() ? {
        resumeText: data.text,
        filename: data.filename || file.name,
      } : null);
      toast.success(`Extracted text from ${data.filename}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to upload file'));
    } finally {
      setUploadingFile(false);
    }
  };

  const handleAnalyze = async () => {
    if (!resumeText.trim()) {
      toast.error('Please enter or upload your resume');
      return;
    }

    setLoading(true);
    try {
      const resumeTextToAnalyze = await attemptSavePendingUploadedResume(resumeText);
      const data = await api.analyzeResume(resumeTextToAnalyze, null);
      setResult(data);
      toast.success('Analysis complete!');
    } catch (error) {
      if (isEmailVerificationRequiredError(error)) {
        toast.error(error.response?.data?.detail || error.message || 'Please verify your email before using this feature.');
        navigate('/dashboard');
      } else if (error.response?.status === 403) {
        toast.error(error.response.data.detail);
        navigate('/pricing');
      } else {
        toast.error(error.response?.data?.detail || 'Analysis failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-destructive';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-success';
    if (score >= 60) return 'bg-warning';
    return 'bg-destructive';
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <MainMenu />
          <button
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold">Resume Analysis</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {!result ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Data Usage Notice */}
            <div className="flex items-start gap-2 p-3 bg-surface border border-border rounded-lg text-sm">
              <Info className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <p className="text-muted-foreground">
                Resumes are processed using AI to generate feedback. Avoid including sensitive personal data like SSN or financial details.
              </p>
            </div>

            {/* Resume Input */}
            <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-semibold">Your Resume</h2>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile}
                  className="flex items-center gap-2 text-primary hover:text-primary-hover transition-colors text-sm"
                  data-testid="upload-btn"
                >
                  {uploadingFile ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Upload File
                </button>
              </div>
              {savedResumeUiEnabled && (
                <SavedResumeSelector
                  savedResumes={savedResumes}
                  selectedSavedResumeId={selectedSavedResumeId}
                  onSelectSavedResume={handleSavedResumeSelect}
                  loading={savedResumesLoading}
                  disabled={uploadingFile || loadingSavedResume}
                />
              )}
              <p className="text-xs text-muted-foreground mb-3">
                Supported formats: {SUPPORTED_UPLOAD_FORMAT_LABEL}. {LEGACY_DOC_GUIDANCE}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept={UPLOAD_ACCEPT}
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-input"
              />
              <textarea
                value={resumeText}
                onChange={(e) => {
                  setResumeText(e.target.value);
                  setReplacementNotice('');
                  setPendingUploadedResumeSave(null);
                  setSelectedSavedResumeId('');
                }}
                placeholder="Paste your resume text here, or upload a PDF file..."
                className="w-full h-64 p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none font-mono text-sm"
                data-testid="resume-textarea"
              />
              {replacementNotice && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {replacementNotice}
                </p>
              )}
            </div>

            {/* Analyze Button */}
            <button
              onClick={handleAnalyze}
              disabled={loading || !resumeText.trim()}
              className="w-full bg-primary text-white py-4 rounded-xl font-medium hover:bg-primary-hover transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="analyze-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <TrendingUp className="w-5 h-5" />
                  Analyze Resume
                </>
              )}
            </button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* AI Disclosure */}
            <AIDisclosure />

            {/* Score Card */}
            <div className="bg-white border border-border rounded-xl p-8 shadow-soft text-center">
              <h2 className="font-heading text-lg font-semibold mb-4">Overall Score</h2>
              <div className="relative w-40 h-40 mx-auto mb-4">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="currentColor"
                    strokeWidth="10"
                    fill="none"
                    className="text-surface"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="currentColor"
                    strokeWidth="10"
                    fill="none"
                    className={getScoreBg(result.overall_score)}
                    strokeDasharray={`${(result.overall_score / 100) * 440} 440`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`font-heading text-5xl font-bold ${getScoreColor(result.overall_score)}`}>
                    {result.overall_score}
                  </span>
                  <span className="text-muted-foreground">out of 100</span>
                </div>
              </div>
            </div>

            {/* Results Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Strengths */}
              <div className="bg-white border border-border rounded-xl p-6 shadow-soft" data-testid="strengths-card">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                    <Check className="w-5 h-5 text-success" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold">Strengths</h3>
                </div>
                <ul className="space-y-2">
                  {result.strengths.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Weaknesses */}
              <div className="bg-white border border-border rounded-xl p-6 shadow-soft" data-testid="weaknesses-card">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-destructive/10 rounded-lg flex items-center justify-center">
                    <X className="w-5 h-5 text-destructive" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold">Weaknesses</h3>
                </div>
                <ul className="space-y-2">
                  {result.weaknesses.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <X className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Suggested Keywords */}
              <div className="bg-white border border-border rounded-xl p-6 shadow-soft" data-testid="keywords-card">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-warning" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold">Suggested Keywords to Strengthen Resume</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {result.missing_keywords.map((keyword, i) => (
                    <span key={i} className="px-3 py-1 bg-warning/10 text-warning rounded-full text-sm">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>

              {/* Suggestions */}
              <div className="bg-white border border-border rounded-xl p-6 shadow-soft" data-testid="suggestions-card">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold">Improvements</h3>
                </div>
                <ul className="space-y-2">
                  {result.suggested_improvements.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Target className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* How to Get 100 */}
            <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 rounded-xl p-6" data-testid="reach-100-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-heading text-lg font-semibold">How to Reach 100</h3>
              </div>
              <ul className="space-y-2">
                {result.suggestions_to_100.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="w-5 h-5 bg-primary text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">
                      {i + 1}
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setResult(null)}
                className="flex-1 bg-white border border-border py-3 rounded-xl font-medium hover:bg-surface transition-all"
                data-testid="analyze-another-btn"
              >
                Analyze Another Resume
              </button>
              {(effectivePlan === 'pro' || effectivePlan === 'premium') && (
                <button
                  onClick={() => navigate('/compare')}
                  className="flex-1 bg-primary text-white py-3 rounded-xl font-medium hover:bg-primary-hover transition-all"
                  data-testid="compare-btn"
                >
                  Compare to Job Description
                </button>
              )}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
